<template>
  <el-dialog
    :model-value="visible"
    width="420px"
    class="client-auth-dialog"
    :close-on-click-modal="false"
    @update:model-value="handleClose"
    @closed="handleClosed"
  >
    <template #header>
      <div class="dialog-heading">
        <span>Backrooms</span>
        <strong>注册</strong>
      </div>
    </template>

    <el-form
      ref="registerFormRef"
      :model="registerForm"
      :rules="registerRules"
      label-position="top"
      class="auth-form"
    >
      <el-form-item label="账号" prop="account">
        <el-input
          v-model="registerForm.account"
          placeholder="请输入 10 位账号"
          maxlength="10"
          clearable
          size="large"
        />
      </el-form-item>
      <el-form-item label="密码" prop="password">
        <el-input
          v-model="registerForm.password"
          type="password"
          placeholder="请输入 8-20 位密码"
          maxlength="20"
          show-password
          clearable
          size="large"
        />
      </el-form-item>
      <el-form-item label="确认密码" prop="confirmPassword">
        <el-input
          v-model="registerForm.confirmPassword"
          type="password"
          placeholder="请再次输入密码"
          maxlength="20"
          show-password
          clearable
          size="large"
        />
      </el-form-item>
      <el-button type="primary" size="large" class="submit-btn" :loading="loading" @click="handleRegister">
        注册
      </el-button>
      <button class="link-btn" type="button" @click="switchToLogin">已有账号？去登录</button>
    </el-form>
  </el-dialog>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';

interface Props {
  visible: boolean;
}

interface Emits {
  (e: 'update:visible', value: boolean): void;
  (e: 'register', account: string, password: string): void;
  (e: 'switchToLogin'): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const registerForm = reactive({
  account: '',
  password: '',
  confirmPassword: ''
});
const registerFormRef = ref<FormInstance>();
const loading = ref(false);

const validateConfirmPassword = (_rule: any, value: string, callback: (error?: Error) => void) => {
  if (!value) callback(new Error('请再次输入密码'));
  else if (value !== registerForm.password) callback(new Error('两次输入的密码不一致'));
  else callback();
};

const registerRules: FormRules = {
  account: [
    { required: true, message: '请输入账号', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9]{10}$/, message: '账号只能是 10 位字母或数字', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 8, max: 20, message: '密码长度为 8-20 位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请再次输入密码', trigger: 'blur' },
    { min: 8, max: 20, message: '确认密码长度为 8-20 位', trigger: 'blur' },
    { validator: validateConfirmPassword, trigger: 'blur' }
  ]
};

const handleClose = (value: boolean) => {
  if (!value) emit('update:visible', false);
};

const handleClosed = () => {
  registerForm.account = '';
  registerForm.password = '';
  registerForm.confirmPassword = '';
  registerFormRef.value?.clearValidate();
  registerFormRef.value?.resetFields();
};

const handleRegister = async () => {
  if (!registerFormRef.value) return;
  try {
    await registerFormRef.value.validate();
    loading.value = true;
    emit('register', registerForm.account, registerForm.password);
  } finally {
    loading.value = false;
  }
};

const switchToLogin = () => {
  emit('switchToLogin');
};
</script>

<style scoped>
:global(.client-auth-dialog .el-dialog) {
  width: min(92vw, 420px);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.055), rgba(157, 92, 255, 0.026)),
    rgba(10, 15, 28, 0.96);
  border: 1px solid rgba(0, 240, 216, 0.24);
  border-radius: 8px;
  box-shadow: 0 30px 110px rgba(0, 0, 0, 0.5), 0 0 34px rgba(0, 240, 216, 0.08);
  backdrop-filter: blur(28px) saturate(125%);
}

:global(.client-auth-dialog .el-dialog__header) {
  margin: 0;
  padding: 24px 24px 0;
}

:global(.client-auth-dialog .el-dialog__body) {
  padding: 22px 24px 24px;
}

.dialog-heading span,
.auth-form :deep(.el-form-item__label) {
  color: var(--cyber-muted, #8992a9);
}

.dialog-heading strong {
  display: block;
  margin-top: 6px;
  color: var(--cyber-text, #e8edf7);
  font-size: 28px;
  letter-spacing: 0;
}

.auth-form :deep(.el-input__wrapper) {
  border-radius: 8px;
  background: rgba(8, 13, 26, 0.72);
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.2) inset;
}

.auth-form :deep(.el-input__inner) {
  color: var(--cyber-text, #e8edf7);
}

.submit-btn {
  width: 100%;
  margin-top: 4px;
  border-radius: 8px;
}

.link-btn {
  display: block;
  width: 100%;
  margin-top: 14px;
  color: var(--cyber-muted, #8992a9);
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: color 0.3s ease, text-shadow 0.3s ease;
}

.link-btn:hover {
  color: var(--cyber-cyan, #00f0d8);
  text-shadow: 0 0 12px rgba(0, 240, 216, 0.2);
}
</style>
